import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { JOB_STEP, SLOT_LABEL, formatDay, formatDateTime, jobStatusLabel, todayUk } from '@nu/shared';
import RouteMap from '../../components/RouteMap.jsx';
import { addDays } from '../bookings/shared.jsx';
import Directions from './Directions.jsx';
import SwitchJob from './SwitchJob.jsx';
import { dayView, useLocalJobs } from './progress.js';
import { useSchedule } from './useSchedule.js';
import VanPrompt from './VanPrompt.jsx';

// The schedule (app Home): any day's jobs for the team, in the office's
// order, in one card: day buttons, a simple map, a one-line list (number,
// slot, status), and ONE Directions arrow: on the row of the job the team is
// on now (once it is In progress the arrow goes). Every other job not started
// has a Switch button: do it next instead, with a reason. Tapping a row opens
// the job. Everything works with or without signal.

const isoDay = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v : null);
const weekday = (day) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
const statusClass = (s) => (JOB_STEP[s] === 0 && s !== 'paused' ? 'not_started' : s);

export default function Schedule() {
  const today = todayUk();
  const [params, setParams] = useSearchParams();
  const day = isoDay(params.get('day')) || today;
  const setDay = (d) => setParams(d === today ? {} : { day: d }, { replace: true });
  const navigate = useNavigate();
  const { data, savedAt, error, reload } = useSchedule(day);
  const local = useLocalJobs();
  const { jobs, current } = dayView(data, local);
  const [asking, setAsking] = useState(null);
  const open = (j) => navigate(`/schedule/job/${j.id}${day === today ? '' : `?day=${day}`}`);
  const isToday = day === today;

  return (
    <div className="stack schedule">
      <section className="card">
        <div className="day-switch">
          <button type="button" className="btn icon-only" aria-label="Previous day" onClick={() => setDay(addDays(day, -1))}><ChevronLeft size={22} aria-hidden="true" /></button>
          <button
            type="button"
            className={`btn day-today${isToday ? ' current' : ''}`}
            onClick={() => setDay(today)}
            aria-label={isToday ? `Today, ${formatDay(day)}` : `${formatDay(day)}. Go back to today`}
          >
            {isToday ? 'Today' : weekday(day)} {formatDay(day)}
          </button>
          <button type="button" className="btn icon-only" aria-label="Next day" onClick={() => setDay(addDays(day, 1))}><ChevronRight size={22} aria-hidden="true" /></button>
        </div>
        {savedAt && <p className="notice-line notice-warn" role="status">No signal: this is the copy saved at {formatDateTime(savedAt)}.</p>}
        {!data && error && <div className="empty"><p>Couldn&apos;t load your jobs. Check your signal.</p><button type="button" className="btn" onClick={reload}>Try again</button></div>}
        {!data && !error && <p className="muted">Loading…</p>}
        {data && !data.team && <p className="muted">You&apos;re not in a team on this day.</p>}
        {data?.team && !jobs.length && <p className="muted">No jobs on this day.</p>}
        {isToday && <VanPrompt prompt={data?.prompt} />}

        {jobs.length > 0 && (
          <RouteMap
            simple
            height={260}
            // The logo's lime, outlined in its navy so it shows on the map.
            routes={data.line?.length ? [{ key: 'day', colour: '#D9E021', casing: '#0B2340', line: data.line }] : []}
            stops={jobs.map((j) => ({ key: j.id, coord: j.coord, label: j.seq, colour: '#0B2340', onClick: () => open(j) }))}
          />
        )}

        {jobs.length > 0 && (
          <ol className="job-list">
            {jobs.map((j) => (
              <li key={j.id} className={j.id === current?.id ? 'is-current' : ''}>
                <div className="job-row">
                  <button type="button" className="job-main" onClick={() => open(j)} aria-label={`Job ${j.seq}, ${j.reference}, ${jobStatusLabel(j.status)}${j.id === current?.id ? ', current job' : ''}`}>
                    <span className="seq">{j.seq}</span>
                    <strong className="job-slot">{SLOT_LABEL[j.slot] || 'No slot'}</strong>
                    <span className={`status status-${statusClass(j.status)}`}>{jobStatusLabel(j.status)}</span>
                    <span className="job-tag">{j.id === current?.id ? 'Now' : j.skippedAt && JOB_STEP[j.status] === 0 ? 'Later' : ''}</span>
                  </button>
                  {j.id === current?.id && JOB_STEP[j.status] < 2 && <Directions job={j} className="btn icon-only" iconOnly />}
                  {j.id !== current?.id && JOB_STEP[j.status] === 0 && JOB_STEP[current?.status] !== 2 && (
                    <button type="button" className="btn icon-only row-switch" aria-label={`Do job ${j.seq} next instead`} title="Do this job next" onClick={() => (j.status === 'paused' ? open(j) : setAsking(j))}>
                      <ArrowLeftRight size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {jobs.length > 0 && !current && <p className="muted center">All jobs on this day are done.</p>}
      </section>

      {asking && (
        <SwitchJob
          day={day}
          job={asking}
          current={current}
          onView={() => { setAsking(null); open(asking); }}
          onClose={() => setAsking(null)}
        />
      )}
    </div>
  );
}
